import React from "react";
import { OnChange } from "react-final-form-listeners";
import { Form } from "react-final-form";
import { mat4, quat } from "gl-matrix";
import { RatioBox } from "@syos-music/common";
import { CheckboxField } from "@syos-music/form";

import { Alert, Box, CircularProgress } from "@mui/material";

import { initShaderProgram } from "./gl-utils";
import { useStl } from "./stl-utils";

import Camera from "./camera";
import GBuffer from "./gbuffer";
import Ssao from "./ssao";

export default function MouthpieceViewer({ url }) {
  const { stlData, loading, error } = useStl(url);

  const [clipZ, _setClipZ] = React.useState(false);
  const clipZRef = React.useRef(clipZ);
  const setClipZ = (bool) => {
    clipZRef.current = bool;
    _setClipZ(bool);
  };

  const [msaaEnabled, setMsaaEnabled] = React.useState(false);
  const [ssaoEnabled, setSsaoEnabled] = React.useState(true);

  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!stlData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("webgl2", { antialias: false });
    ctx.getExtension("EXT_color_buffer_float");
    ctx.getExtension("EXT_color_buffer_half_float");

    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;

    const camera = new Camera(canvas);

    const gbuffer = new GBuffer(ctx, msaaEnabled ? 4 : 0);
    let ssao;
    if (ssaoEnabled) ssao = new Ssao(ctx);

    let ambientProgramInfo;

    let stlBuffers = {};
    let stlProgramInfo;
    let stlMatrix = mat4.create();

    async function init(gl) {
      // Lighting
      const ambientProgram = initShaderProgram(
        gl,
        require("./shaders/splash.vert").default,
        require("./shaders/ambient.frag").default.replace(
          "#define USE_SSAO",
          ssaoEnabled ? "#define USE_SSAO" : ""
        )
      );
      ambientProgramInfo = {
        program: ambientProgram,
        attribLocations: {},
        uniformLocations: {
          gPosition: gl.getUniformLocation(ambientProgram, "u_gPosition"),
          gNormal: gl.getUniformLocation(ambientProgram, "u_gNormal"),
          gAlbedo: gl.getUniformLocation(ambientProgram, "u_gAlbedo"),
          ssao: gl.getUniformLocation(ambientProgram, "u_Ssao"),
        },
      };

      // Load STL
      stlBuffers.positions = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, stlBuffers.positions);
      gl.bufferData(gl.ARRAY_BUFFER, stlData.positions, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);

      stlBuffers.normals = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, stlBuffers.normals);
      gl.bufferData(gl.ARRAY_BUFFER, stlData.normals, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);

      const shaderProgram = initShaderProgram(
        gl,
        require("./shaders/model.vert").default,
        require("./shaders/model.frag").default
      );

      stlProgramInfo = {
        program: shaderProgram,
        attribLocations: {
          positions: gl.getAttribLocation(shaderProgram, "a_Position"),
          normals: gl.getAttribLocation(shaderProgram, "a_Normal"),
        },
        uniformLocations: {
          clipZ: gl.getUniformLocation(shaderProgram, "u_ClipZ"),
          model: gl.getUniformLocation(shaderProgram, "u_Model"),
          view: gl.getUniformLocation(shaderProgram, "u_View"),
          projection: gl.getUniformLocation(shaderProgram, "u_Projection"),
        },
      };

      // Convert to y-up & center model
      mat4.rotateX(stlMatrix, stlMatrix, -Math.PI / 2);
      mat4.rotateY(stlMatrix, stlMatrix, -Math.PI / 2);
      mat4.translate(stlMatrix, stlMatrix, [
        -stlData.center[0],
        -stlData.center[1],
        -stlData.center[2],
      ]);
    }

    function renderStl(gl) {
      // Render object
      gl.bindBuffer(gl.ARRAY_BUFFER, stlBuffers.positions);
      gl.vertexAttribPointer(
        stlProgramInfo.attribLocations.positions,
        3, // num components
        gl.FLOAT, // type
        false, // normalize
        0, // stride
        0 // offset
      );
      gl.enableVertexAttribArray(stlProgramInfo.attribLocations.positions);

      gl.bindBuffer(gl.ARRAY_BUFFER, stlBuffers.normals);
      gl.vertexAttribPointer(
        stlProgramInfo.attribLocations.normals,
        3, // num components
        gl.FLOAT, // type
        false, // normalize
        0, // stride
        0 // offset
      );
      gl.enableVertexAttribArray(stlProgramInfo.attribLocations.normals);

      gl.useProgram(stlProgramInfo.program);

      gl.uniform1i(stlProgramInfo.uniformLocations.clipZ, clipZRef.current);

      gl.uniformMatrix4fv(
        stlProgramInfo.uniformLocations.model,
        false,
        stlMatrix
      );
      gl.uniformMatrix4fv(
        stlProgramInfo.uniformLocations.view,
        false,
        camera.getView()
      );
      gl.uniformMatrix4fv(
        stlProgramInfo.uniformLocations.projection,
        false,
        camera.projection
      );

      gl.drawArrays(
        gl.TRIANGLES,
        0, // offset
        stlData.vertexCount
      );
    }

    function draw(gl, time) {
      gbuffer.initPass(gl);

      renderStl(gl);

      gbuffer.commit(gl);

      // SSAO
      if (ssao) ssao.draw(gl, gbuffer, camera.projection);

      // Ambient
      gl.clearColor(0.0, 0.0, 0.0, 0.0);

      gl.useProgram(ambientProgramInfo.program);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, gbuffer.outputPosition);
      gl.uniform1i(ambientProgramInfo.uniformLocations.gPosition, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, gbuffer.outputNormal);
      gl.uniform1i(ambientProgramInfo.uniformLocations.gNormal, 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, gbuffer.outputAlbedo);
      gl.uniform1i(ambientProgramInfo.uniformLocations.gAlbedo, 2);

      if (ssao) {
        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, ssao.texture);
        gl.uniform1i(ambientProgramInfo.uniformLocations.ssao, 3);
      }

      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    let animationFrameId;
    function renderFrame(time) {
      animationFrameId = requestAnimationFrame(renderFrame);
      draw(ctx, time);
    }

    init(ctx)
      .then(() => (animationFrameId = requestAnimationFrame(renderFrame)))
      .catch(console.error);

    let mousePressed = false;
    const handleMouseDown = () => (mousePressed = true);
    const handleMouseUp = () => (mousePressed = false);
    const handleMouseMove = (event) => {
      if (!mousePressed) return;

      let m = mat4.create();
      let q = quat.fromEuler(
        quat.create(),
        event.movementY,
        event.movementX,
        0
      );
      mat4.fromQuat(m, q);

      mat4.mul(stlMatrix, m, stlMatrix);
    };

    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mousemove", handleMouseMove);

    return () => {
      cancelAnimationFrame(animationFrameId);

      camera.cleanup();
      gbuffer.cleanup(ctx);
      if (ssao) ssao.cleanup(ctx);

      ctx.deleteBuffer(stlBuffers.positions);
      ctx.deleteBuffer(stlBuffers.normals);

      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [url, stlData, msaaEnabled, ssaoEnabled]);

  return (
    <Form
      onSubmit={() => {}}
      initialValues={{
        clipZ,
        msaa: msaaEnabled,
        ssao: ssaoEnabled,
      }}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit }) => (
        <RatioBox
          ratio={16 / 10}
          sx={{
            "& .RatioBox-content": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Alert severity="error">{error.toString()}</Alert>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                position: "absolute",
                top: 0,
                left: (t) => t.spacing(2),
                zIndex: 2,
              }}
            >
              <CheckboxField label="Clip Z" name="clipZ" />
              <OnChange name="clipZ">{setClipZ}</OnChange>

              <CheckboxField label="MSAA (4x)" name="msaa" />
              <OnChange name="msaa">{setMsaaEnabled}</OnChange>

              <CheckboxField label="SSAO" name="ssao" />
              <OnChange name="ssao">{setSsaoEnabled}</OnChange>
            </Box>
          )}

          <Box
            component="canvas"
            ref={canvasRef}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
            }}
          />
        </RatioBox>
      )}
    </Form>
  );
}
